import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import { api } from './api';
import { SiteDataContext, useSiteData } from './SiteDataContext';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (s) => s,
  translating: false
});

const STORAGE_KEY = 'site_lang';
const CACHE_KEY = 'site_translations_v3';

const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); }
  catch { return {}; }
};

const saveCache = (cache) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); }
  catch { /* quota */ }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');
  const [dict, setDict] = useState(() => {
    const c = loadCache();
    const initial = localStorage.getItem(STORAGE_KEY) || 'en';
    return initial === 'en' ? {} : (c[initial] || {});
  });
  const [translating, setTranslating] = useState(false);
  const pendingRef = useRef(new Set());
  const flushTimer = useRef(null);

  // When lang changes, hydrate dict from cache (instant)
  useEffect(() => {
    if (lang === 'en') {
      setDict({});
      return;
    }
    const c = loadCache();
    setDict(c[lang] || {});
  }, [lang]);

  const persist = useCallback((mapping, forLang) => {
    const c = loadCache();
    c[forLang] = { ...(c[forLang] || {}), ...mapping };
    saveCache(c);
  }, []);

  const flush = useCallback(async () => {
    if (lang === 'en') {
      pendingRef.current = new Set();
      return;
    }
    const batch = Array.from(pendingRef.current);
    if (batch.length === 0) return;
    pendingRef.current = new Set();
    setTranslating(true);
    try {
      const { data } = await api.post('/translate', {
        texts: batch,
        target_lang: lang,
        source_lang: 'en'
      });
      const mapping = {};
      batch.forEach((s, i) => { mapping[s] = data.translations[i] || s; });
      setDict((prev) => ({ ...prev, ...mapping }));
      persist(mapping, lang);
    } catch (e) {
      // graceful fallback: leave originals
      // eslint-disable-next-line no-console
      console.warn('translate batch failed', e);
    } finally {
      setTranslating(false);
    }
  }, [lang, persist]);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, 250);
  }, [flush]);

  const t = useCallback((s) => {
    if (s == null || typeof s !== 'string') return s;
    if (lang === 'en') return s;
    if (!s.trim() || !/[A-Za-z]/.test(s)) return s;
    if (dict[s] !== undefined) return dict[s];
    if (!pendingRef.current.has(s)) {
      pendingRef.current.add(s);
      scheduleFlush();
    }
    return s; // return original until translation arrives
  }, [lang, dict, scheduleFlush]);

  const setLang = useCallback((l) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
  }, []);

  const value = useMemo(() => ({ lang, setLang, t, translating }),
    [lang, setLang, t, translating]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);

// ----------------------------------------------------------------------------
// TranslatedSiteDataProvider: wraps the public app and re-provides SiteDataContext
// with all string leaves passed through t(). Components keep using useSiteData()
// unchanged and automatically get the Somali version when lang='so'.
// ----------------------------------------------------------------------------
const SKIP_KEYS = new Set([
  'id', '_id', 'slug', 'icon', 'image_url', 'file_url', 'logo_url', 'photo_url',
  'map_image_url', 'href', 'order', 'size', 'file_size', 'content_type',
  'created_at', 'date', 'day', 'month', 'year', 'primary_link',
  'secondary_link', 'tertiary_link', 'copyright_template'
]);

const deepTranslate = (node, t) => {
  if (node == null) return node;
  if (typeof node === 'string') return t(node);
  if (Array.isArray(node)) return node.map((x) => deepTranslate(x, t));
  if (typeof node === 'object') {
    const out = {};
    for (const k of Object.keys(node)) {
      if (SKIP_KEYS.has(k)) {
        out[k] = node[k];
      } else {
        out[k] = deepTranslate(node[k], t);
      }
    }
    return out;
  }
  return node;
};

export const TranslatedSiteDataProvider = ({ children }) => {
  const raw = useSiteData();
  const { t, lang } = useLang();
  const value = useMemo(() => {
    if (lang === 'en') return raw;
    return {
      ...raw,
      settings: deepTranslate(raw.settings, t),
      slides: deepTranslate(raw.slides, t),
      news: deepTranslate(raw.news, t),
      events: deepTranslate(raw.events, t),
      ministries: deepTranslate(raw.ministries, t),
      investment: deepTranslate(raw.investment, t)
    };
  }, [raw, t, lang]);
  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
};
